"use client";

// Above-fold chat surface for SEO landing pages. Reframes the existing
// SEOAgentBridge "click to chat" card as an actual chat box: persona pill,
// 3 starter prompt chips, a real textarea, a send button. The chat itself
// still lives inside /free-kundali/ (ChatPanel) because we need birth
// details for grounded answers — on submit we stash the prompt + intent
// into sessionStorage and navigate, and the post-kundali ChatPanel reads
// seo_entry_prompt and auto-sends it as the first user message.
//
// Persona naming follows the rule: real human Jyotishi name + photo, never
// generic "AI Vedic Scholar" labels (memory: feedback_ai_persona_naming).

import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import {
  getSEOIntentConfig,
  getSEOIntentConfigForSource,
  type SEOIntent,
  type SEOIntentConfig,
} from "@/lib/seo-intents";
import { scorePurchaseIntent } from "@/lib/purchase-intent-score";
import { safeSessionSet } from "@/lib/browser-storage";

interface InlineChatPreviewProps {
  intent?: SEOIntent;
  sourcePath?: string;
  currentPage?: string;
  /** Override the persona shown in the pill. Defaults to Pandit-ji. */
  personaName?: string;
  personaSubline?: string;
  headlineLevel?: "h1" | "p";
  headline?: ReactNode;
  subhead?: ReactNode;
  className?: string;
  onIntentCaptured?: (entry: { config: SEOIntentConfig; sourcePath: string }) => void;
}

const DEFAULT_PERSONA_NAME = "Pandit-ji";
const DEFAULT_PERSONA_SUBLINE = "AI Jyotishi · Replies in seconds";
const PLACEMENT = "inline_chat_preview";
type SEOBridgeViewPayload = {
  source_path: string;
  intent: SEOIntent;
  page_category: string;
  offer_type: string;
  plan_id?: string;
  placement: typeof PLACEMENT;
  purchase_intent_score?: number;
  purchase_intent_band?: string;
  purchase_intent_reason?: string;
};
type SEOBridgeClickPayload = SEOBridgeViewPayload & { target: string };

function trackInlineChatViewed(payload: SEOBridgeViewPayload) {
  void import("@/lib/analytics-seo-bridge")
    .then(({ trackSEOAgentBridgeViewed }) => trackSEOAgentBridgeViewed(payload))
    .catch(() => {});
}

function trackInlineChatClicked(payload: SEOBridgeClickPayload) {
  void import("@/lib/analytics-seo-bridge")
    .then(({ trackSEOAgentBridgeClicked }) => trackSEOAgentBridgeClicked(payload))
    .catch(() => {});
}

function trackInlineIntentCaptured(payload: {
  source_path: string;
  intent: SEOIntent;
  page_category: string;
  target: string;
  placement: typeof PLACEMENT;
}) {
  void import("@/lib/analytics-seo-bridge")
    .then(({ trackSEOIntentCaptured }) => trackSEOIntentCaptured(payload))
    .catch(() => {});
}

function currentPath(fallback?: string) {
  if (typeof window === "undefined") return fallback || "";
  return window.location.pathname || fallback || "";
}

function PersonaPill({
  personaName,
  personaSubline,
}: {
  personaName: string;
  personaSubline: string;
}) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[20px]"
        style={{ background: "var(--ish-accent)", color: "#fff" }}
        aria-hidden
      >
        ॐ
      </div>
      <div className="min-w-0">
        <p className="text-[15px] font-semibold leading-tight" style={{ color: "var(--ish-dark)" }}>
          {personaName}
        </p>
        <p
          className="mt-0.5 flex items-center gap-1.5 text-[12px]"
          style={{ color: "var(--ish-text-muted)" }}
        >
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: "#22c55e" }}
            aria-hidden
          />
          {personaSubline}
        </p>
      </div>
    </div>
  );
}

function StarterPromptChips({
  prompts,
  onSelect,
}: {
  prompts: string[];
  onSelect: (prompt: string) => void;
}) {
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {prompts.map((prompt) => (
        <button
          key={prompt}
          type="button"
          onClick={() => onSelect(prompt)}
          className="rounded-full px-3 py-1.5 text-[12px] sm:text-[13px] font-medium transition hover:opacity-90"
          style={{
            background: "color-mix(in srgb, var(--ish-bg) 82%, var(--ish-accent) 18%)",
            border: "1px solid color-mix(in srgb, var(--ish-accent) 24%, transparent)",
            color: "var(--ish-dark)",
          }}
        >
          {prompt}
        </button>
      ))}
    </div>
  );
}

function ChatPromptForm({
  draft,
  personaName,
  setDraft,
  onSubmit,
}: {
  draft: string;
  personaName: string;
  setDraft: (value: string) => void;
  onSubmit: (prompt: string) => void;
}) {
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(draft);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
      <label className="sr-only" htmlFor="inline-chat-input">
        Ask {personaName} anything
      </label>
      <textarea
        id="inline-chat-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={`Type your question for ${personaName}…`}
        rows={2}
        className="flex-1 resize-none rounded-lg px-3 py-2 text-[14px] outline-none transition focus:ring-2"
        style={{
          background: "var(--ish-bg)",
          border: "1px solid color-mix(in srgb, var(--ish-accent) 22%, transparent)",
          color: "var(--ish-dark)",
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit(draft);
          }
        }}
      />
      <button
        type="submit"
        disabled={!draft.trim()}
        className="shrink-0 rounded-lg px-5 py-2.5 text-[14px] font-semibold transition disabled:opacity-50"
        style={{ background: "var(--ish-accent)", color: "#fff" }}
      >
        Ask →
      </button>
    </form>
  );
}

export default function InlineChatPreview({
  intent,
  sourcePath,
  currentPage,
  personaName = DEFAULT_PERSONA_NAME,
  personaSubline = DEFAULT_PERSONA_SUBLINE,
  headlineLevel = "p",
  headline,
  subhead,
  className = "",
  onIntentCaptured,
}: InlineChatPreviewProps) {
  const router = useRouter();
  const [draft, setDraft] = useState("");

  const config = useMemo(() => {
    const sourceConfig = getSEOIntentConfigForSource(currentPage || sourcePath);
    if (intent) {
      const intentConfig = getSEOIntentConfig(intent);
      return sourceConfig.intent === intent ? sourceConfig : intentConfig;
    }
    return sourceConfig;
  }, [currentPage, intent, sourcePath]);

  const resolvedSource = sourcePath || (currentPage ? `/${currentPage}` : "");
  const purchaseIntent = useMemo(
    () =>
      scorePurchaseIntent({
        intent: config.intent,
        sourcePath: resolvedSource || config.pageCategory,
        primaryOffer: config.primaryOffer,
      }),
    [config, resolvedSource]
  );

  useEffect(() => {
    const path = currentPath(resolvedSource || config.pageCategory);
    trackInlineChatViewed({
      source_path: path,
      intent: config.intent,
      page_category: config.pageCategory,
      offer_type: config.primaryOffer,
      plan_id: config.reportPlanId,
      placement: PLACEMENT,
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
  }, [config, purchaseIntent, resolvedSource]);

  function fire(prompt: string) {
    const path = currentPath(resolvedSource || config.pageCategory);
    const finalPrompt = prompt.trim() || config.prompt;
    const capturedConfig =
      finalPrompt === config.prompt ? config : { ...config, prompt: finalPrompt };
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", path);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", finalPrompt);
      safeSessionSet("seo_entry_auto_send", "1");
      safeSessionSet("seo_entry_purchase_intent_score", String(purchaseIntent.score));
      safeSessionSet("seo_entry_purchase_intent_band", purchaseIntent.band);
      safeSessionSet("seo_entry_purchase_intent_reason", purchaseIntent.reason);
    } catch {
      // Storage unavailable — the destination still works, just without auto-send.
    }
    trackInlineChatClicked({
      source_path: path,
      intent: config.intent,
      page_category: config.pageCategory,
      offer_type: config.primaryOffer,
      plan_id: config.reportPlanId,
      target: config.target,
      placement: PLACEMENT,
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
    trackInlineIntentCaptured({
      source_path: path,
      intent: config.intent,
      page_category: config.pageCategory,
      target: config.target,
      placement: PLACEMENT,
    });
    onIntentCaptured?.({ config: capturedConfig, sourcePath: path });
    // If the current page already has a #start-reading anchor (e.g.
    // /free-astrology-chat/ now hosts the KundaliFormCard inline) scroll to
    // it instead of cross-page navigating. Keeps the user in the chat flow
    // they just initiated — Pandit-ji's persona pill above the form
    // makes it read as a continuation, not a redirect to a new page. We
    // dispatch `seo-intent-refresh` so the KundaliFormCard on the same
    // page re-reads sessionStorage and surfaces the user's prompt in its
    // seoEntry banner (without the event, the form mounts before the
    // prompt is stashed and ignores it).
    // Fallback for surfaces without an inline form: /ai-astrologer/#start-reading.
    if (typeof window !== "undefined") {
      const inlineForm = document.getElementById("start-reading");
      if (inlineForm) {
        if (!onIntentCaptured) {
          window.dispatchEvent(new Event("seo-intent-refresh"));
        }
        inlineForm.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    router.push("/ai-astrologer/#start-reading");
  }

  function handleStarterPrompt(prompt: string) {
    setDraft(prompt);
    fire(prompt);
  }

  // Starter prompts come from the SEO intent config so they're surface-aware
  // (panchang vs marriage vs career vs generic).
  const starterPrompts = config.starterPrompts.slice(0, 3);
  const headlineContent = headline ?? config.headline;
  const headlineClassName = `${subhead ? "mb-2" : "mb-4"} leading-snug text-[15px] sm:text-[16px] ${
    headlineLevel === "h1" ? "font-bold text-[22px] sm:text-[26px]" : ""
  }`;

  return (
    <section
      data-agent-bridge="inline"
      className={`mx-auto max-w-2xl px-4 sm:px-6 py-8 ${className}`}
    >
      <div
        className="rounded-2xl p-5 sm:p-6"
        style={{
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--ish-accent) 12%, transparent) 0%, color-mix(in srgb, var(--ish-accent) 4%, var(--ish-bg)) 100%)",
          border: "1px solid color-mix(in srgb, var(--ish-accent) 30%, transparent)",
        }}
      >
        <PersonaPill personaName={personaName} personaSubline={personaSubline} />

        {headlineLevel === "h1" ? (
          <h1 className={headlineClassName} style={{ color: "var(--ish-dark)" }}>
            {headlineContent}
          </h1>
        ) : (
          <p className={headlineClassName} style={{ color: "var(--ish-dark)" }}>
            {headlineContent}
          </p>
        )}
        {subhead ? (
          <p
            className="mb-4 text-[13px] leading-relaxed"
            style={{ color: "var(--ish-text-muted)" }}
          >
            {subhead}
          </p>
        ) : null}

        <StarterPromptChips prompts={starterPrompts} onSelect={handleStarterPrompt} />
        <ChatPromptForm
          draft={draft}
          personaName={personaName}
          setDraft={setDraft}
          onSubmit={fire}
        />

        <p className="mt-3 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
          Birth details on the next step. Free · No signup · 24×7.
        </p>
      </div>
    </section>
  );
}
